import { qfRestGet } from './qfClient';

/**
 * Sector-level reads of the tape: the returns behind the rotation scatter.
 *
 * **LIVE** — QuantForge `GET /v1/steamroom/sector-rotation`, no parameters.
 *
 * ### QF serves computed returns, not raw closes
 *
 * The original contract asked for ~400 days of daily closes per ETF and derived
 * every horizon client-side. QF returns the finished returns instead, because
 * they already compute and render these exact figures in their own product.
 * Two clients deriving returns independently from the same closes is how the
 * two surfaces end up disagreeing about what "1W" means, with no authority to
 * settle it.
 *
 * The arithmetic therefore disappears rather than moving: `ret_1d` and `ret_5d`
 * are the scatter's axes directly, and every table column arrives ready. What
 * we cannot do from this response is anything QF did not precompute — raw
 * closes remain available on request if a future panel needs them.
 *
 * ⚠️ **One request for all eleven, and it is now unconditional.** The reference
 * implementation fanned out with `useQueries` — eleven parallel requests per
 * page load, against a provider capped at three concurrent requests **per
 * account**. QF serves this from their own store with no vendor call on the
 * request path, so the cap no longer applies to this panel at all.
 */

/** Rank-based badge QF assigns across the eleven. We classify our own; unused. */
export type SectorStatus = 'leading' | 'advancing' | 'flat' | 'declining' | 'lagging';

/**
 * One sector's returns.
 *
 * ⚠️ **Returns are PERCENT, not fractions.** `+1.48` means +1.48%. This is the
 * opposite convention to `/v1/symbol-summary`, whose `change_pct` is `0.0758`
 * for +7.58% — and both are consumed on the same page. Do not multiply by 100.
 *
 * ⚠️ **The horizon returns are ex-today**, computed against completed sessions,
 * so YTD is measured from the previous year's final close. `ret_1d` is the live
 * intraday move. That split is deliberate on QF's side and is what the table
 * expects: the 1D column moves during the session while the rest do not.
 */
export interface SectorReturns {
  /** ETF ticker — "XLK". */
  symbol: string;
  /** QF's display name. Ignored; see `SECTORS` in constants/sectors.ts. */
  name: string;
  /** Static S&P weight. Ignored for the same reason — ours drives bubble size. */
  weight: number;
  last: number | null;
  ret_1d: number | null;
  ret_5d: number | null;
  ret_1w: number | null;
  ret_1m: number | null;
  ret_3m: number | null;
  ret_6m: number | null;
  ret_ytd: number | null;
  status: SectorStatus | null;
}

export interface SectorRotationResponse {
  /** ISO-8601 instant the figures describe. */
  as_of: string;
  /** Session date, YYYY-MM-DD ET. */
  session_date: string;
  /** All eleven, sorted by `ret_1d` descending. */
  sectors: SectorReturns[];
}

/** Returns keyed by ETF ticker, which is how the panel looks them up. */
export type SectorReturnsByEtf = Record<string, SectorReturns>;

/**
 * ⚠️ **Namespaced, and gated.** QF retired their own Sector Rotation pane and
 * deleted the bare `/v1/sector-rotation` route with it (their #898/#900), which
 * 404'd this panel. They restored the compute for us at the `/v1/steamroom/*`
 * path instead — in their convention the `steamroom` URL segment is what marks a
 * route as locked, so the bare path is not coming back.
 *
 * The practical difference from the old open path: admission is now
 * permission-based, not just signature-based. A signed bearer whose user holds
 * none of `steamroom` / `Alpha` / `jdash_access` / `wsjonly` gets a 403 rather
 * than data. `fetchSectorReturns` throws on that, which the panel renders as
 * unavailable — correct, but it is an entitlement problem, not an outage.
 */
const SECTOR_ROTATION_PATH = '/v1/steamroom/sector-rotation';

/**
 * Rotation returns for the eleven sector ETFs, keyed by ticker.
 *
 * Throws on failure rather than returning an empty map: a sector with no data
 * and an unreachable feed both render as a missing bubble, and only one of them
 * means the sector did not move.
 */
export async function fetchSectorReturns(): Promise<SectorReturnsByEtf> {
  const body = await qfRestGet<SectorRotationResponse>(SECTOR_ROTATION_PATH);
  const rows = body?.sectors ?? [];
  return Object.fromEntries(rows.map((s) => [s.symbol, s]));
}
